// Utility service for common application functions
import StorageService from './storageService';
import ProfileService from './profileService';

class AppUtilsService {
  /**
   * Get the current user's organization ID
   * First tries localStorage, then fetches from user profile if needed
   * @param {string} userId - The user's ID
   * @param {string} token - Auth token
   * @returns {Promise<string|null>} Organization ID or null if not found
   */
  static async getOrganizationId(userId = null, token = null) {
    try {
      // First try to get from localStorage
      let organizationId = StorageService.getOrganizationId();
      
      if (organizationId) {
        return organizationId;
      }

      // If not in localStorage and we have user info, fetch from profile
      if (userId && token) {
        try {
          const userProfile = await ProfileService.getUserProfile(userId, token);
          organizationId = userProfile?.organizationId;
          
          // Save to localStorage for future use
          if (organizationId) {
            StorageService.setOrganizationId(organizationId);
            return organizationId;
          }
        } catch (error) {
          console.error('Error fetching organization ID from user profile:', error);
        }
      }

      return null;
    } catch (error) {
      console.error('Error getting organization ID:', error);
      return null;
    }
  }

  /**
   * Clear user data on logout
   */
  static clearUserData() {
    StorageService.clearAllData();
  }

  /**
   * Format phone number for display
   * @param {string} phone - Phone number
   * @returns {string} Formatted phone number
   */
  static formatPhoneNumber(phone) {
    if (!phone) return '';
    
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');
    
    // Format based on length (assuming Mexican format)
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length === 12 && digits.startsWith('52')) {
      // Mexican international format
      return `+52 (${digits.slice(2, 5)}) ${digits.slice(5, 8)}-${digits.slice(8)}`;
    }
    
    return phone; // Return original if can't format
  }

  /**
   * Validate email format
   * @param {string} email - Email to validate
   * @returns {boolean} True if valid email format
   */
  static isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Debounce function for search inputs
   * @param {Function} func - Function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @returns {Function} Debounced function
   */
  static debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Check if user is authenticated and has required organization
   * @param {object} auth - Auth context object
   * @returns {object} Status object with isAuthenticated and hasOrganization flags
   */
  static getUserStatus(auth) {
    const isAuthenticated = !!auth?.user?.access_token;
    const hasOrganization = !!StorageService.getOrganizationId();
    
    return {
      isAuthenticated,
      hasOrganization,
      requiresSetup: isAuthenticated && !hasOrganization
    };
  }
}

export default AppUtilsService;
